// Local font retrieval (Local Font Access API).
// queryLocalFonts() is only available on Chromium-based browsers + https + user gesture + granted permission;
// when unavailable or denied, fall back to a built-in list of common fonts. Once granted, Chromium exposes the fonts to CSS rendering.

export const FALLBACK_FONTS: string[] = [
  // Sans-serif
  'Arial', 'Helvetica', 'Helvetica Neue', 'Verdana', 'Tahoma', 'Trebuchet MS', 'Segoe UI',
  'Futura', 'Avenir', 'Optima', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Noto Sans SC',
  // Serif
  'Times New Roman', 'Georgia', 'Garamond', 'Palatino', 'Didot', 'Songti SC', 'SimSun', 'Noto Serif SC',
  // Monospace
  'Courier New', 'Consolas', 'Menlo', 'Monaco', 'JetBrains Mono', 'Fira Code',
  // Display
  'Impact', 'Comic Sans MS',
];

declare global {
  interface Window {
    queryLocalFonts?: () => Promise<Array<{ family: string; fullName: string; style: string }>>;
  }
}

let cache: string[] | null = null;
let inflight: Promise<string[]> | null = null;

/** Fetch local font family list (deduped + sorted). Requires a user gesture; falls back to FALLBACK_FONTS on failure. */
export function loadLocalFonts(): Promise<string[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  if (typeof window === 'undefined' || !window.queryLocalFonts) {
    return Promise.resolve(FALLBACK_FONTS);
  }
  inflight = window.queryLocalFonts()
    .then(fonts => {
      const fams = Array.from(new Set(fonts.map(f => f.family)));
      fams.sort((a, b) => a.localeCompare(b));
      // Merge in common entries from the built-in list that may still be missing after permission is granted (deduped)
      const merged = Array.from(new Set([...fams, ...FALLBACK_FONTS.filter(f => fams.includes(f))]))
        .sort((a, b) => a.localeCompare(b));
      cache = merged;
      return merged;
    })
    .catch(() => FALLBACK_FONTS) // permission denied / unsupported
    .finally(() => { inflight = null; });
  return inflight;
}

export function localFontsSupported(): boolean {
  return typeof window !== 'undefined' && !!window.queryLocalFonts;
}
